import CAAnimationData from '@/static/pet/CAanimation.json';
import AnimationData from '@/static/pet/discounts_new.json';
import GustoAnimationData from '@/static/pet/discounts_new_gusto.json';
import { useLottie } from '@lemonade-hq/cdk';
import type { AnimationSegment } from 'lottie-web';
import type React from 'react';
import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { isAffiliateDiscountValid, PARTNER_NAMES, useAffiliateDiscount } from './hooks/useAffiliateDiscount';
import { useAllowedRegion } from './useAllowedRegion';

const Banner = styled.div`
  width: 266px;
  height: 55px;
  position: relative;
  margin-top: 7px;
  margin-bottom: -18px;
  content-visibility: auto;
  contain-intrinsic-size: auto 55px;
`;

const AnimationRef = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const NOT_ALLOWED_REGION_SEGMENTS: AnimationSegment[] = [
  [0, 152],
  [54, 152],
];

const ALLOWED_REGION_SEGMENTS: AnimationSegment[] = [
  [0, 395],
  [105, 395],
];

const GUSTO_REGION_SEGMENTS: AnimationSegment[] = [
  [0, 525],
  [137, 525],
];

const DefaultAnimationBanner: React.FC<{
  ariaLabel: string;
  isAllowedRegion: boolean;
}> = ({ ariaLabel, isAllowedRegion }) => {
  const animationRef = useRef<HTMLDivElement>(null);
  const lottieRef = useLottie(animationRef, {
    animationData: isAllowedRegion ? AnimationData : CAAnimationData,
    lottieConfig: {
      autoplay: false,
      loop: true,
    },
  });

  useEffect(() => {
    if (lottieRef) {
      lottieRef.setSpeed(1.2);
      lottieRef.playSegments(isAllowedRegion ? ALLOWED_REGION_SEGMENTS : NOT_ALLOWED_REGION_SEGMENTS, true);
    }
  }, [lottieRef, isAllowedRegion]);

  return <AnimationRef ref={animationRef} aria-label={ariaLabel} role="img" />;
};

const GustoAnimationBanner: React.FC<{
  ariaLabel: string;
}> = ({ ariaLabel }) => {
  const animationRef = useRef<HTMLDivElement>(null);
  const lottieRef = useLottie(animationRef, {
    animationData: GustoAnimationData,
    lottieConfig: {
      autoplay: false,
      loop: true,
    },
  });

  useEffect(() => {
    if (lottieRef) {
      lottieRef.setSpeed(1.2);
      lottieRef.playSegments(GUSTO_REGION_SEGMENTS, true);
    }
  }, [lottieRef]);

  return <AnimationRef ref={animationRef} aria-label={ariaLabel} role="img" />;
};

export const DiscountBanner: React.FC = () => {
  const { affiliateDiscount, isLoading: isAffiliateDiscountLoading } = useAffiliateDiscount();
  const isAllowedRegion = useAllowedRegion();

  const ariaLabel = useMemo(() => {
    const baseLabel = isAllowedRegion
      ? '10% bundle discount, 5% multi-pet discount, 5% annual discount'
      : '10% bundle discount';

    if (affiliateDiscount && isAffiliateDiscountValid(affiliateDiscount)) {
      return `${baseLabel}, ${affiliateDiscount.discountPercent}% ${PARTNER_NAMES[affiliateDiscount.partnerCode]} discount`;
    }

    return baseLabel;
  }, [isAllowedRegion, affiliateDiscount]);

  if (isAffiliateDiscountLoading) return null;

  return (
    <Banner>
      {affiliateDiscount?.partnerCode === 'gusto' ? (
        <GustoAnimationBanner ariaLabel={ariaLabel} />
      ) : (
        <DefaultAnimationBanner ariaLabel={ariaLabel} isAllowedRegion={isAllowedRegion} />
      )}
    </Banner>
  );
};
